import React, { useState, useRef } from 'react';
import Modal from '../Modal';
import { saudeService } from '../../services/saude.service';
import {
  CloudArrowUpIcon,
  SparklesIcon,
  ArrowPathIcon,
  TrashIcon,
  DocumentTextIcon,
  PhotoIcon,
  CheckCircleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

interface QueuedFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  errorMessage?: string;
}

interface UploadAtestadoModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId: number;
  colaboradorIdPreSelecionado?: number;
  onSuccess: () => void;
  toast: {
    success: (msg: string) => void;
    error: (msg: string) => void;
  };
}

export default function UploadAtestadoModal({
  isOpen,
  onClose,
  empresaId,
  colaboradorIdPreSelecionado,
  onSuccess,
  toast,
}: UploadAtestadoModalProps) {
  const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFilesAdded = (incomingFiles: FileList | File[]) => {
    const validExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'webp'];
    const addedList: QueuedFile[] = [];

    Array.from(incomingFiles).forEach((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!validExtensions.includes(ext)) {
        toast.error(`Formato .${ext} não suportado para o arquivo ${file.name}.`);
        return;
      }

      // Evita duplicatas exatas na mesma fila
      const isAlreadyInQueue = queuedFiles.some(
        (q) => q.file.name === file.name && q.file.size === file.size,
      );
      if (!isAlreadyInQueue) {
        addedList.push({
          id: `${Date.now()}-${Math.random().toString(36).substring(2, 7)}-${file.name}`,
          file,
          status: 'pending',
        });
      }
    });

    if (addedList.length > 0) {
      setQueuedFiles((prev) => [...prev, ...addedList]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesAdded(e.dataTransfer.files);
    }
  };

  const handleRemoveFile = (id: string) => {
    if (submitting) return;
    setQueuedFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const handleClearAll = () => {
    if (submitting) return;
    setQueuedFiles([]);
    setUploadProgress(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (queuedFiles.length === 0) {
      toast.error('Adicione pelo menos um arquivo de atestado.');
      return;
    }

    setSubmitting(true);
    setUploadProgress({ current: 0, total: queuedFiles.length });

    try {
      const rawFiles = queuedFiles.map((q) => q.file);

      // Atualiza status visual para enviando
      setQueuedFiles((prev) =>
        prev.map((item) => ({ ...item, status: 'uploading' })),
      );

      const res = await saudeService.uploadLote(
        rawFiles,
        empresaId,
        colaboradorIdPreSelecionado,
      );

      // Marca sucesso em todos
      setQueuedFiles((prev) =>
        prev.map((item) => ({ ...item, status: 'success' })),
      );

      toast.success(
        res.total === 1
          ? 'Atestado recebido com sucesso! A IA já iniciou a leitura óptica e auditoria.'
          : `${res.total} atestados recebidos em lote! A IA iniciou o processamento e a separação automática.`,
      );

      const redirecionados = res.atestados?.filter((a) => a.redirecionado_grupo_economico) || [];
      if (redirecionados.length > 0) {
        const nomesEmpresas = Array.from(
          new Set(redirecionados.map((a) => a.empresa_destino_nome || 'outra unidade do grupo')),
        ).join(', ');
        toast.success(
          `🏢 Grupo Econômico: ${
            redirecionados.length === 1
              ? `Colaborador identificado na empresa "${nomesEmpresas}". O atestado foi direcionado para a unidade correta.`
              : `${redirecionados.length} atestados foram identificados como pertencentes a colaboradores de ${nomesEmpresas} e direcionados automaticamente.`
          }`,
        );
      }

      setTimeout(() => {
        onSuccess();
        onClose();
        setQueuedFiles([]);
        setSubmitting(false);
        setUploadProgress(null);
      }, 700);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Falha ao processar o envio dos atestados.';
      toast.error(msg);
      setQueuedFiles((prev) =>
        prev.map((item) => ({ ...item, status: 'error', errorMessage: msg })),
      );
      setSubmitting(false);
      setUploadProgress(null);
    }
  };

  const totalBytes = queuedFiles.reduce((acc, q) => acc + q.file.size, 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title="Upload de Atestados Médicos (Extração e Auditoria por IA)"
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Banner de Inteligência Pericial */}
        <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-teal-50 border border-indigo-200/70 rounded-xl p-4 flex items-start gap-3.5 shadow-xs">
          <div className="p-2.5 bg-indigo-600 rounded-xl text-white shadow-xs shrink-0">
            <SparklesIcon className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-bold text-gray-900">
                Auditoria 4D & Extração Automática com IA
              </h4>
              <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                Zero Digitação RH
              </span>
              {colaboradorIdPreSelecionado ? (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                  Vínculo com Colaborador Atual
                </span>
              ) : null}
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Arraste um ou múltiplos atestados (fotos ou PDFs). Nossa inteligência pericial fará a leitura óptica automática, identificação do colaborador, data, CID-10, CRM e acionamento de verificação institucional.
            </p>
          </div>
        </div>

        {/* Zona de Arraste e Seleção de Arquivos (Dropzone) */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !submitting && fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-2xl p-7 text-center transition-all cursor-pointer flex flex-col items-center justify-center select-none ${
            isDragOver
              ? 'border-indigo-500 bg-indigo-50/70 scale-[1.01] shadow-inner ring-4 ring-indigo-100'
              : queuedFiles.length > 0
              ? 'border-indigo-300 bg-gray-50/60 hover:bg-indigo-50/20 hover:border-indigo-400'
              : 'border-gray-300 bg-gray-50/40 hover:border-indigo-400 hover:bg-gray-50 shadow-xs'
          } ${submitting ? 'pointer-events-none opacity-60' : ''}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,image/png,image/jpeg,image/webp"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFilesAdded(e.target.files);
                e.target.value = '';
              }
            }}
            className="hidden"
          />

          <div
            className={`p-3.5 rounded-2xl shadow-xs transition-transform duration-200 mb-3 ${
              isDragOver
                ? 'bg-indigo-600 text-white scale-110'
                : queuedFiles.length > 0
                ? 'bg-indigo-100 text-indigo-700'
                : 'bg-white text-indigo-600 border border-gray-200'
            }`}
          >
            <CloudArrowUpIcon className="w-8 h-8" />
          </div>

          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-800">
              {isDragOver
                ? 'Solte os atestados para adicionar à fila'
                : 'Arraste os documentos aqui ou clique para selecionar'}
            </p>
            <p className="text-xs text-gray-500">
              Formatos aceitos: <strong className="font-medium text-gray-700">PDF, JPG, PNG e WebP</strong> (até 15MB por arquivo)
            </p>
            <p className="text-[11px] text-indigo-600 font-medium pt-0.5">
              Suporta envio individual ou em lote de múltiplos atestados simultaneamente
            </p>
          </div>
        </div>

        {/* Fila de Documentos Selecionados */}
        {queuedFiles.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center justify-between text-xs px-1">
              <span className="font-bold text-gray-700 flex items-center gap-1.5">
                <span>Documentos na Fila ({queuedFiles.length})</span>
                <span className="text-gray-400 font-normal">• {formatFileSize(totalBytes)}</span>
              </span>

              {!submitting && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition"
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                    Adicionar mais
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="text-[11px] font-medium text-red-600 hover:text-red-700 transition"
                  >
                    Limpar tudo
                  </button>
                </div>
              )}
            </div>

            <div className="max-h-56 overflow-y-auto space-y-2 pr-1 rounded-xl border border-gray-200 bg-white p-2 divide-y divide-gray-100 shadow-xs">
              {queuedFiles.map((item, idx) => {
                const isPdf = item.file.name.toLowerCase().endsWith('.pdf');
                return (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between gap-3 p-2 rounded-lg transition text-xs ${
                      idx > 0 ? 'pt-2' : ''
                    } hover:bg-gray-50`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div
                        className={`p-2 rounded-lg shrink-0 ${
                          isPdf
                            ? 'bg-rose-50 text-rose-600 border border-rose-100'
                            : 'bg-blue-50 text-blue-600 border border-blue-100'
                        }`}
                      >
                        {isPdf ? (
                          <DocumentTextIcon className="w-4 h-4" />
                        ) : (
                          <PhotoIcon className="w-4 h-4" />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {item.file.name}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {formatFileSize(item.file.size)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {item.status === 'pending' && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700">
                          Pronto para IA
                        </span>
                      )}
                      {item.status === 'uploading' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 animate-pulse">
                          <ArrowPathIcon className="w-3 h-3 animate-spin" />
                          Enviando...
                        </span>
                      )}
                      {item.status === 'success' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700">
                          <CheckCircleIcon className="w-3 h-3 text-emerald-600" />
                          Enviado
                        </span>
                      )}
                      {item.status === 'error' && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-50 text-rose-700">
                          Erro
                        </span>
                      )}

                      {!submitting && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(item.id)}
                          className="p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                          title="Remover arquivo da fila"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Rodapé e Ações */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="text-[11px] text-gray-500">
            {queuedFiles.length > 0 ? (
              <span>
                <strong className="text-gray-700">{queuedFiles.length}</strong> {queuedFiles.length === 1 ? 'atestado preparado' : 'atestados preparados'}
              </span>
            ) : (
              <span>Nenhum documento selecionado</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={submitting || queuedFiles.length === 0}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-md shadow-indigo-200 hover:shadow-indigo-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin text-white" />
                  <span>
                    {uploadProgress
                      ? `Enviando lote (${queuedFiles.length} arquivos)...`
                      : 'Iniciando fluxo de IA...'}
                  </span>
                </>
              ) : (
                <>
                  <SparklesIcon className="w-4 h-4 text-indigo-200" />
                  <span>
                    {queuedFiles.length <= 1
                      ? 'Iniciar Processamento com IA'
                      : `Iniciar Processamento em Lote com IA (${queuedFiles.length})`}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
